import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { cn } from "utils";
import useApp from "hooks/useApp";
import { CHANGE_STATE } from "reducers/app.reducer";
import axiosInstance from "../axios/axiosInstance";
import routes from "constants/routes";
import { formatRoute } from "utils";
import TosModal, { hasTosAccepted } from "../components/TosModal";

const PendingApprovalBanner = () => {
    const [dismissed, setDismissed] = useState(false);
    const navigate = useNavigate();
    const companyId = localStorage.getItem('companyId');

    const { data: pendingAccounts = [] } = useQuery({
        queryKey: ['pending-approvals', companyId],
        queryFn: async () => {
            const res = await axiosInstance.get(`/auth/pending-approvals/${companyId}`);
            return res.data ?? [];
        },
        enabled: !!companyId,
        refetchInterval: 30000,
    });

    const count = pendingAccounts.length;
    if (dismissed || count === 0) return null;

    return (
        <div style={{
            background: '#fef9c3', border: '1px solid #fbbf24',
            padding: '10px 20px', display: 'flex', alignItems: 'center',
            gap: 12, fontSize: 13, color: '#78350f', flexShrink: 0,
        }}>
            <span style={{ fontSize: 16 }}>⏳</span>
            <span style={{ flex: 1 }}>
                <strong>{count} pending onboarding{count !== 1 ? 's' : ''}</strong> awaiting your approval.
            </span>
            <button
                onClick={() => navigate(formatRoute(routes.ONBOARDING.HOME))}
                style={{
                    background: '#1B75F0', color: '#fff', border: 'none',
                    borderRadius: 6, padding: '5px 14px', fontWeight: 600,
                    fontSize: 12, cursor: 'pointer',
                }}
            >
                Review →
            </button>
            <button
                onClick={() => setDismissed(true)}
                title="Dismiss"
                style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#92400e', fontSize: 18, lineHeight: 1, padding: '0 4px',
                }}
            >
                ×
            </button>
        </div>
    );
};

const DashboardLayout = () => {
    const { isSidebarOpen, dispatch } = useApp();
    const [tosAccepted, setTosAccepted] = useState(hasTosAccepted);
    const { pathname } = useLocation();

    const isMobile = () => window.innerWidth <= 768;

    // Auto-close sidebar on mobile at mount
    useEffect(() => {
        if (isMobile()) {
            dispatch({ type: CHANGE_STATE, payload: { isSidebarOpen: false } });
        }
    }, []);

    // Close sidebar on route change (mobile)
    useEffect(() => {
        if (isMobile()) {
            dispatch({ type: CHANGE_STATE, payload: { isSidebarOpen: false } });
        }
    }, [pathname]);

    const closeSidebar = () => dispatch({ type: CHANGE_STATE, payload: { isSidebarOpen: false } });

    return (
        <div className="dashboard container-fluid p-0">
            {!tosAccepted && <TosModal onAccept={() => setTosAccepted(true)} />}
            {/* Mobile backdrop */}
            {isSidebarOpen && (
                <div className="mobile-sidebar-backdrop" onClick={closeSidebar} style={{ display: 'none' }} />
            )}
            <div className="app-container d-flex">
                <Sidebar />
                <div className={cn("main-content header-container", isSidebarOpen ? "open" : "closed")}>
                    <Header />
                    <PendingApprovalBanner />
                    <div className="content">
                        <Outlet />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardLayout;
